import moment from 'moment-timezone';
import { maskMoney, unmaskMoney } from '../../../../services/masks';
import { FieldDiff, PolicyFormState, AdminEditPolicyModalData, CoverageFormItem } from './types';

export const CANCELLATION_STATUSES = ['CANCELED', 'CANCELLED', 'CANCELATION_REQUEST'];

export const PAYMENT_TYPE_OPTIONS = [
  { key: 'AutomaticDebit', value: 'AutomaticDebit', text: 'Débito automático' },
  { key: 'Invoice', value: 'Invoice', text: 'Fatura' },
  { key: 'Boleto', value: 'Boleto', text: 'Boleto' }
];

const moneyToForm = (value?: number | null): string => {
  if (value === null || value === undefined || value === ('' as any)) {
    return '';
  }
  // `float: true` keeps the decimal point from Number#toFixed; without it,
  // maskMoney strips dots and turns 90.85 into 9.085,00.
  return maskMoney(String(Number(value).toFixed(2)), true);
};

const parseMoney = (value: string): number | null => {
  if (!value || !String(value).trim()) {
    return null;
  }
  const parsed = unmaskMoney(String(value));
  return Number.isFinite(parsed) ? parsed : null;
};

const toDate = (value?: string | Date | null): Date | null => {
  if (!value) {
    return null;
  }
  const date = moment(value);
  return date.isValid() ? date.toDate() : null;
};

const formatDateDisplay = (value: Date | null): string => {
  if (!value) {
    return '—';
  }
  return moment(value).format('DD/MM/YYYY');
};

const formatMoneyDisplay = (value: string): string => {
  if (!value || !String(value).trim()) {
    return '—';
  }
  return `R$ ${value}`;
};

const coverageToForm = (coverage: NonNullable<AdminEditPolicyModalData['coverages']>[number]): CoverageFormItem => ({
  id: coverage.id,
  coverageName: coverage.coverageName || '',
  referenceCode: coverage.referenceCode || '',
  value: moneyToForm(coverage.value),
  totalPremium: moneyToForm(coverage.totalPremium),
  maxValue: moneyToForm(coverage.maxValue),
  minValue: moneyToForm(coverage.minValue),
  enabled: coverage.enabled !== false,
  description: coverage.description || '',
  _isNew: false
});

export const mapPolicyToFormState = (data: AdminEditPolicyModalData): PolicyFormState => ({
  policyId: data.policyId,
  analysisId: data.analysisId,
  status: data.status || '',
  policyNumber: data.policyNumber || '',
  externalProposalCode: data.externalProposalCode || '',
  externalPolicyCode: data.externalPolicyCode || '',
  referenceCode: data.referenceCode || '',
  observations: data.observations || '',
  paymentType: data.paymentType || '',
  installments: data.installments != null ? String(data.installments) : '',
  policyPeriodStart: toDate(data.policyPeriodStart),
  policyPeriodEnd: toDate(data.policyPeriodEnd),
  commercialPremium: moneyToForm(data.commercialPremium),
  grossPremium: moneyToForm(data.grossPremium),
  minPremium: moneyToForm(data.minPremium),
  maxPremium: moneyToForm(data.maxPremium),
  iof: moneyToForm(data.iof),
  assistanceValue: moneyToForm(data.assistanceValue),
  value: moneyToForm(data.value),
  valueFirstInstallment: moneyToForm(data.valueFirstInstallment),
  analysisInstallments:
    data.analysisInstallments != null ? String(data.analysisInstallments) : '',
  coverages: (data.coverages || []).map(coverageToForm),
  cancellationReason: '',
  canceledAt: null,
  cancellationObservations: '',
  refundValue: ''
});

const normalizeComparable = (value: unknown): string => {
  if (value instanceof Date) {
    return formatDateDisplay(value);
  }
  if (value === null || value === undefined) {
    return '';
  }
  return String(value).trim();
};

export const computeFieldDiffs = (
  original: PolicyFormState,
  current: PolicyFormState,
  statusLabel: (status: string) => string
): FieldDiff[] => {
  const diffs: FieldDiff[] = [];

  const pushIfChanged = (
    key: keyof PolicyFormState,
    label: string,
    tab: FieldDiff['tab'],
    format: (value: any) => string = (v) => normalizeComparable(v) || '—'
  ) => {
    const oldValue = format(original[key]);
    const newValue = format(current[key]);
    if (oldValue !== newValue) {
      diffs.push({ key, label, tab, oldValue, newValue });
    }
  };

  pushIfChanged('status', 'Status', 'identification', (v) => statusLabel(v) || '—');
  pushIfChanged('policyNumber', 'Número da apólice', 'identification');
  pushIfChanged('externalProposalCode', 'Código externo da proposta', 'identification');
  pushIfChanged('externalPolicyCode', 'Código externo da apólice', 'identification');
  pushIfChanged('referenceCode', 'Código de referência', 'identification');
  pushIfChanged('paymentType', 'Tipo de pagamento', 'identification');
  pushIfChanged('installments', 'Parcelas (apólice)', 'identification');
  pushIfChanged('observations', 'Observações', 'identification');
  pushIfChanged('policyPeriodStart', 'Início da vigência', 'identification', formatDateDisplay);
  pushIfChanged('policyPeriodEnd', 'Fim da vigência', 'identification', formatDateDisplay);

  pushIfChanged('commercialPremium', 'Prêmio comercial', 'values', formatMoneyDisplay);
  pushIfChanged('grossPremium', 'Prêmio bruto', 'values', formatMoneyDisplay);
  pushIfChanged('minPremium', 'Prêmio mínimo', 'values', formatMoneyDisplay);
  pushIfChanged('maxPremium', 'Prêmio máximo', 'values', formatMoneyDisplay);
  pushIfChanged('iof', 'IOF', 'values', formatMoneyDisplay);
  pushIfChanged('assistanceValue', 'Assistência', 'values', formatMoneyDisplay);
  pushIfChanged('value', 'Valor da parcela', 'values', formatMoneyDisplay);
  pushIfChanged('analysisInstallments', 'Parcelas (análise)', 'values');

  const originalCoverages = original.coverages || [];
  const currentCoverages = current.coverages || [];

  currentCoverages.forEach((coverage) => {
    if (!coverage.id) {
      return;
    }

    const previous = originalCoverages.find((item) => item.id === coverage.id);
    if (!previous) {
      return;
    }

    if (previous.value !== coverage.value) {
      diffs.push({
        key: `coverage-value-${coverage.id}`,
        label: `Cobertura (${coverage.coverageName || coverage.id}) — Valor`,
        tab: 'coverages',
        oldValue: formatMoneyDisplay(previous.value),
        newValue: formatMoneyDisplay(coverage.value)
      });
    }
    if (previous.totalPremium !== coverage.totalPremium) {
      diffs.push({
        key: `coverage-premium-${coverage.id}`,
        label: `Cobertura (${coverage.coverageName || coverage.id}) — Prêmio`,
        tab: 'coverages',
        oldValue: formatMoneyDisplay(previous.totalPremium),
        newValue: formatMoneyDisplay(coverage.totalPremium)
      });
    }
    if (previous.enabled !== coverage.enabled) {
      diffs.push({
        key: `coverage-enabled-${coverage.id}`,
        label: `Cobertura (${coverage.coverageName || coverage.id}) — Ativa`,
        tab: 'coverages',
        oldValue: previous.enabled ? 'Sim' : 'Não',
        newValue: coverage.enabled ? 'Sim' : 'Não'
      });
    }
  });

  if (isTransitioningToCancellation(original.status, current.status)) {
    if (current.cancellationReason.trim()) {
      diffs.push({
        key: 'cancellationReason',
        label: 'Motivo do cancelamento',
        tab: 'cancellation',
        oldValue: '—',
        newValue: current.cancellationReason
      });
    }
    if (current.canceledAt) {
      diffs.push({
        key: 'canceledAt',
        label: 'Data do cancelamento',
        tab: 'cancellation',
        oldValue: '—',
        newValue: formatDateDisplay(current.canceledAt)
      });
    }
    if (current.refundValue.trim()) {
      diffs.push({
        key: 'refundValue',
        label: 'Valor de reembolso',
        tab: 'cancellation',
        oldValue: '—',
        newValue: formatMoneyDisplay(current.refundValue)
      });
    }
    if (current.cancellationObservations.trim()) {
      diffs.push({
        key: 'cancellationObservations',
        label: 'Observações do cancelamento',
        tab: 'cancellation',
        oldValue: '—',
        newValue: current.cancellationObservations
      });
    }
  }

  return diffs;
};

export const getDirtyTabs = (diffs: FieldDiff[]) => {
  return {
    identification: diffs.some((diff) => diff.tab === 'identification'),
    values: diffs.some((diff) => diff.tab === 'values'),
    coverages: diffs.some((diff) => diff.tab === 'coverages'),
    cancellation: diffs.some((diff) => diff.tab === 'cancellation')
  };
};

export const isTransitioningToCancellation = (
  originalStatus: string,
  currentStatus: string
): boolean =>
  currentStatus !== originalStatus && CANCELLATION_STATUSES.includes(currentStatus);

export const buildUpdatePayload = (form: PolicyFormState, originalStatus: string) => {
  const payload: any = {
    policy: {
      id: form.policyId,
      status: form.status || undefined,
      policyNumber: form.policyNumber || null,
      externalProposalCode: form.externalProposalCode || null,
      externalPolicyCode: form.externalPolicyCode || null,
      observations: form.observations || null,
      paymentType: form.paymentType || undefined,
      installments: form.installments ? Number(form.installments) : undefined,
      policyPeriodStart: form.policyPeriodStart
        ? moment(form.policyPeriodStart).toISOString()
        : undefined,
      policyPeriodEnd: form.policyPeriodEnd
        ? moment(form.policyPeriodEnd).toISOString()
        : undefined
    },
    analysis: {
      id: form.analysisId,
      referenceCode: form.referenceCode || null,
      commercialPremium: parseMoney(form.commercialPremium),
      grossPremium: parseMoney(form.grossPremium),
      minPremium: parseMoney(form.minPremium),
      maxPremium: parseMoney(form.maxPremium),
      iof: parseMoney(form.iof),
      assistanceValue: parseMoney(form.assistanceValue),
      value: parseMoney(form.value),
      installments: form.analysisInstallments
        ? Number(form.analysisInstallments)
        : null
    },
    coverages: form.coverages
      .filter((coverage) => !!coverage.id)
      .map((coverage) => ({
        id: coverage.id,
        value: parseMoney(coverage.value),
        totalPremium: parseMoney(coverage.totalPremium),
        enabled: coverage.enabled
      }))
  };

  if (isTransitioningToCancellation(originalStatus, form.status)) {
    payload.cancellation = {
      reason: form.cancellationReason.trim(),
      canceledAt: form.canceledAt
        ? moment(form.canceledAt).toISOString()
        : undefined,
      observations: form.cancellationObservations || null,
      refundValue: parseMoney(form.refundValue)
    };
  }

  return payload;
};

export const validateForm = (
  form: PolicyFormState,
  originalStatus: string
): string | null => {
  if (!form.status) {
    return 'Selecione o status da apólice.';
  }

  if (
    isTransitioningToCancellation(originalStatus, form.status) &&
    !form.cancellationReason.trim()
  ) {
    return 'Informe o motivo do cancelamento na aba Cancelamento.';
  }

  for (const coverage of form.coverages) {
    if (!coverage.enabled) {
      continue;
    }
    const value = parseMoney(coverage.value);
    if (value == null || value <= 0) {
      return `A cobertura "${coverage.coverageName || coverage.id}" precisa de um valor maior que zero.`;
    }
  }

  return null;
};

export const summarizeDiffs = (diffs: FieldDiff[]): string => {
  const parts: string[] = [];
  if (diffs.some((diff) => diff.tab === 'identification')) {
    parts.push('identificação/status');
  }
  if (diffs.some((diff) => diff.tab === 'values')) {
    parts.push('valores');
  }
  const coverageCount = diffs.filter((diff) => diff.tab === 'coverages').length;
  if (coverageCount > 0) {
    parts.push(`${coverageCount} alteração(ões) em coberturas`);
  }
  if (diffs.some((diff) => diff.tab === 'cancellation')) {
    parts.push('cancelamento');
  }
  return parts.join(', ') || 'dados';
};

export { maskMoney, parseMoney };
